// Каркас плагина-хоста. Плагин, который показывает что-то поверх всего
// приложения (попап, дровер, плашка), делает одно и то же: кладёт контейнер в
// body, рендерит в нём свой компонент в контексте приложения и — если ему нужен
// адрес страницы — дожидается роутера. Здесь этот каркас лежит один раз, а
// плагины (alert, auth, payments, date-picker, otp и плагины проектов) держат
// только своё: состояние и компонент.
import { h, render, watch } from 'vue';

/**
 * Контейнер в конце страницы и рендер компонента в нём.
 *
 * appContext обязателен: без него компонент живёт вне приложения — не видит ни
 * роутер, ни provide, ни глобальные свойства, и падает на первом же useRoute().
 * На сервере хоста нет: document там отсутствует, а всё это — клиентский UI.
 */
export function mountHost(app, { id, component, props = null, className = '' }) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  const container = document.createElement('div');
  if (id) container.id = id;
  // Слой контейнера: попап поверх попапа (код подтверждения над формой входа)
  // рисуется выше остальных хостов.
  if (className) container.className = className;
  document.body.appendChild(container);
  const vnode = h(component, props);
  vnode.appContext = app._context;
  render(vnode, container);
  return container;
}

/**
 * Роутер приложения, когда он готов. Ждать приходится кадрами: на клиенте
 * app.use(router) идёт ПОЗЖЕ install плагинов (Client.vue монтирует приложение
 * после гидратации), поэтому в момент install роутера на globalProperties ещё
 * нет.
 */
export function onRouterReady(app) {
  // На сервере момента «роутер готов» нет: requestAnimationFrame в node не
  // существует, а продолжение цепочки — клиентский UI и его модульное
  // состояние, общее на все запросы. Промис остаётся неразрешённым, и .then()
  // не выполняется.
  if (typeof window === 'undefined') return new Promise(() => {});
  return new Promise((resolve) => {
    const tick = () => {
      const router = app.config.globalProperties.$router;
      if (!router) { requestAnimationFrame(tick); return; }
      router.isReady().then(() => resolve(router));
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Диплинк-параметр адреса (?chat=…, ?connect=…): значение уходит обработчику и
 * сразу вычищается из URL.
 *
 * Чистка обязательна: без неё параметр прилипает к адресу, и повторный переход
 * по той же ссылке не меняет URL — watcher не срабатывает, экран не открывается.
 * Параметр может прийти списком (?chat=a&chat=b) — берём первое значение.
 */
export function consumeQueryParam(router, name, handler) {
  watch(
    () => router.currentRoute.value.query[name],
    (value) => {
      if (!value) return;
      const route = router.currentRoute.value;
      const { [name]: taken, ...rest } = route.query;
      router.replace({ path: route.path, query: rest });
      handler(Array.isArray(taken) ? taken[0] : taken);
    },
    { immediate: true },
  );
}
